import { buildComposerDraftKey } from "./draftKey";
import type { ComposerDraft, ComposerDraftScope } from "./types";

const SHADOW_PREFIX = "dolphin.composer-draft-shadow.v1.";

const getStorage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

const storageKey = (draftKey: string): string =>
  `${SHADOW_PREFIX}${encodeURIComponent(draftKey)}`;

const isDraft = (
  value: unknown,
  expectedScope?: ComposerDraftScope,
): value is ComposerDraft => {
  if (typeof value !== "object" || value === null) return false;
  const draft = value as Partial<ComposerDraft>;
  if (
    draft.version !== 1 ||
    typeof draft.key !== "string" ||
    typeof draft.content !== "string" ||
    typeof draft.hadAttachments !== "boolean" ||
    typeof draft.createdAt !== "number" ||
    typeof draft.updatedAt !== "number" ||
    !draft.scope
  ) {
    return false;
  }

  try {
    const canonicalKey = buildComposerDraftKey(draft.scope);
    return (
      draft.key === canonicalKey &&
      (!expectedScope || canonicalKey === buildComposerDraftKey(expectedScope))
    );
  } catch {
    return false;
  }
};

const readByStorageKey = (
  storage: Storage,
  key: string,
  expectedScope?: ComposerDraftScope,
): ComposerDraft | null => {
  try {
    const serialized = storage.getItem(key);
    if (!serialized) return null;
    const parsed: unknown = JSON.parse(serialized);
    if (isDraft(parsed, expectedScope)) return parsed;
    storage.removeItem(key);
  } catch {
    // Emergency storage is best-effort and must never break the editor.
  }
  return null;
};

export const writeDraftSessionShadow = (draft: ComposerDraft): void => {
  const storage = getStorage();
  if (!storage) return;
  const key = storageKey(buildComposerDraftKey(draft.scope));
  try {
    if (draft.content.trim().length === 0 && !draft.hadAttachments) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify(draft));
  } catch {
    // IndexedDB remains the primary store when session storage is unavailable.
  }
};

export const readDraftSessionShadow = (
  scope: ComposerDraftScope,
): ComposerDraft | null => {
  const storage = getStorage();
  if (!storage) return null;
  return readByStorageKey(
    storage,
    storageKey(buildComposerDraftKey(scope)),
    scope,
  );
};

export const listActiveDraftSessionShadows = (
  userId: number,
  studyGroupId: number,
): ComposerDraft[] => {
  const storage = getStorage();
  if (!storage) return [];
  const drafts: ComposerDraft[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key?.startsWith(SHADOW_PREFIX)) continue;
      const draft = readByStorageKey(storage, key);
      if (
        draft?.scope.userId === userId &&
        draft.scope.studyGroupId === studyGroupId &&
        (draft.scope.kind === "comment" || draft.scope.kind === "reply") &&
        draft.targetState !== "local-only" &&
        draft.content.trim().length > 0
      ) {
        drafts.push(draft);
      }
    }
  } catch {
    return [];
  }
  return drafts.sort((left, right) => right.updatedAt - left.updatedAt);
};

export const removeDraftSessionShadow = (expected: ComposerDraft): void => {
  const storage = getStorage();
  if (!storage) return;
  const key = storageKey(buildComposerDraftKey(expected.scope));
  const current = readByStorageKey(storage, key, expected.scope);
  if (
    current &&
    current.content === expected.content &&
    current.hadAttachments === expected.hadAttachments &&
    current.updatedAt <= expected.updatedAt &&
    current.targetState === expected.targetState
  ) {
    try {
      storage.removeItem(key);
    } catch {
      // Best-effort cleanup; a stale shadow loses to a newer IndexedDB record.
    }
  }
};

export const removeDraftSessionShadowForScope = (
  scope: ComposerDraftScope,
): void => {
  try {
    getStorage()?.removeItem(storageKey(buildComposerDraftKey(scope)));
  } catch {
    // Best-effort cleanup.
  }
};

export const removeDraftSessionShadowsForUser = (userId: number): void => {
  const storage = getStorage();
  if (!storage) return;
  const keys: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(SHADOW_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      const draft = readByStorageKey(storage, key);
      if (draft?.scope.userId === userId) storage.removeItem(key);
    }
  } catch {
    // Best-effort cleanup; session storage is cleared when the tab closes.
  }
};
